import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Star, ListOrdered, Wrench } from 'lucide-react';
import { SelectorRango, useRangoResenas } from '@/components/reviews/shared';
import type { RangoResenas } from '@/lib/api';

const TABS = [
  // El ranking va primero: es la lista de la que cuelga todo lo demás, y el
  // plan es lo que se hace después de mirarla.
  {
    to: '/reviews',
    label: 'Ranking',
    icon: ListOrdered,
    end: true,
    desc: 'Nota media por alojamiento, de peor a mejor, en escala de 5',
  },
  {
    to: '/reviews/plan',
    label: 'Plan de mejora',
    icon: Wrench,
    end: false,
    desc: 'Lo que los huéspedes piden arreglar, agrupado por alojamiento y tema',
  },
];

/**
 * Ventana de fechas compartida por las dos pestañas.
 *
 * Baja por el `Outlet` en vez de vivir en cada página porque las dos preguntan
 * lo mismo sobre el mismo periodo: si el ranking mira 24 meses y el plan 12, los
 * «temas abiertos» de una tarjeta no cuadran con los puntos de la otra pantalla
 * y el gestor deja de fiarse de las dos.
 */
export interface CtxResenas {
  rango: RangoResenas;
  setRango: (r: RangoResenas) => void;
}

/**
 * Agrupa en una sola entrada del menú las dos vistas de Reseñas —el ranking de
 * alojamientos y el plan de mejora—. Cada pestaña conserva su ruta para que siga
 * siendo enlazable y el botón de atrás del navegador se comporte.
 *
 * El detalle de un alojamiento (`/reviews/l/:listingId`) queda fuera a
 * propósito: dentro gastaría esta cabecera más las pestañas antes de la primera
 * reseña, y en 375 px eso es un cuarto de pantalla de cromo que no se lee.
 */
export default function ReviewsLayout() {
  const { pathname } = useLocation();
  const activa = TABS.find(t => (t.end ? pathname === t.to : pathname.startsWith(t.to))) ?? TABS[0];
  const { rango, setRango } = useRangoResenas();

  return (
    <div>
      {/* `flex-wrap` para que en 375 px el selector caiga a la línea de abajo en
          vez de estrujar el título hasta partirlo por sílabas. */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="shrink-0 p-2 rounded-lg bg-sereno-blue/50 text-sereno-dark">
            <Star size={22} />
          </div>
          {/* `min-w-0` para que el subtítulo largo del plan parta por palabras en
              vez de estirar la fila y sacar el selector fuera de los 375 px. */}
          <div className="min-w-0">
            <h1 className="text-2xl font-bold">Reseñas</h1>
            {/* El subtítulo es el de la pestaña abierta: el título agrupa, pero lo
                que se está mirando sigue explicándose solo. */}
            <p className="text-sm text-sereno-dark/50">{activa.desc}</p>
          </div>
        </div>
        <SelectorRango rango={rango} onChange={setRango} />
      </div>

      <div className="border-b border-slate-200 dark:border-white/10 mb-6">
        <nav className="flex gap-1 -mb-px overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-none">
          {TABS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 sm:px-4 py-3 sm:py-2.5 text-sm whitespace-nowrap border-b-2 transition-colors ${
                  isActive
                    ? 'border-sereno-dark text-sereno-dark font-medium dark:border-white dark:text-white'
                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                }`
              }
            >
              <Icon size={15} />
              {label}
            </NavLink>
          ))}
        </nav>
      </div>

      <Outlet context={{ rango, setRango } satisfies CtxResenas} />
    </div>
  );
}
